import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  getCustomer,
  updateCustomer,
  listStaff,
  createStaff,
  setStaffActive,
  listProjects,
  listBillingAddresses,
  grantCredits,
  getCustomerOverview,
  listCustomerDocuments,
  getCustomerDocument,
  listCustomerGSPRSessions,
  listCustomerPayments,
  listCustomerCreditLedger,
  listCustomerAnalyses,
  getCustomerAnalysis,
  listCustomerChatSessions,
  type Customer,
  type Staff as StaffRow,
  type Project,
  type BillingAddress,
  type CustomerOverview,
  type GeneratedDoc,
  type GSPRSession,
  type Payment,
  type PaymentsResult,
  type CreditEntry,
  type AnalysisEntry,
  type ChatSessionRow,
} from "../api/admin";
import { ortamCoz } from "../lib/ortam";
import Icon from "../components/icons";

type Tab = "ozet" | "bilgi" | "personel" | "proje" | "fatura" | "gspr" | "satinalma" | "analiz";

const PROJECT_STATUS: Record<Project["status"], { label: string; cls: string }> = {
  taslak: { label: "taslak", cls: "off" },
  aktif: { label: "aktif", cls: "ok" },
  tamamlandi: { label: "tamamlandı", cls: "neutral" },
};

// Ödeme durumu — rozet rengi TEK BAŞINA anlam taşımaz, metin de yazılır
// (design-system §2: renk körlüğü + siyah-beyaz baskı).
const PAY_STATUS: Record<Payment["status"], { label: string; cls: string }> = {
  paid: { label: "ödendi", cls: "ok" },
  pending: { label: "bekliyor", cls: "warn" },
  failed: { label: "başarısız", cls: "off" },
};

// Kredi hareketi sebebi — ledger'daki 4 CHECK değerinin okunur karşılığı.
const LEDGER_REASON: Record<CreditEntry["reason"], string> = {
  purchase: "satın alma",
  document_generated: "doküman üretimi",
  admin_grant: "admin tanımı",
  refund: "iade",
};

function fmtDate(s: string): string {
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString("tr-TR");
}

// spr_map JSONB'sinin okunur hâli. Snapshot şeması değişebileceği için tipi
// dar tutmuyoruz; alan yoksa satır yine çizilir (ham JSON'a düşmek yerine).
type SPRDecisionRow = {
  spr?: string;
  title?: string;
  applicability?: string;
  justification?: string;
  required_stds?: string[];
  generated_text?: string;
};

function SPRMapView({ sprMap }: { sprMap: unknown }) {
  const rows: SPRDecisionRow[] = Array.isArray(sprMap)
    ? (sprMap as SPRDecisionRow[])
    : Array.isArray((sprMap as { decisions?: unknown } | null)?.decisions)
      ? ((sprMap as { decisions: SPRDecisionRow[] }).decisions)
      : [];

  if (rows.length === 0) {
    return (
      <>
        <h3 className="section-title">SPR haritası</h3>
        <p className="muted" style={{ fontSize: 13 }}>
          Bu dokümanda SPR haritası yok (doküman hâlâ üretiliyor olabilir).
        </p>
      </>
    );
  }

  return (
    <>
      <h3 className="section-title">SPR haritası · {rows.length} madde</h3>
      {rows.map((d, i) => {
        // "Applicable / Not Applicable" ayrımı renkle DEĞİL metinle taşınır —
        // denetçi çıktıyı siyah-beyaz yazdırır (design-system §2).
        const na = (d.applicability || "").toLowerCase().includes("not");
        return (
          <div className="spr-row" key={d.spr || i}>
            <div className="spr-row-head">
              <span className="spr-no">{d.spr || `#${i + 1}`}</span>
              <span className={"badge " + (na ? "off" : "ok")}>
                {na ? "Not Applicable" : "Applicable"}
              </span>
              {d.title && <span className="muted" style={{ fontSize: 12.5 }}>{d.title}</span>}
            </div>
            {d.required_stds && d.required_stds.length > 0 && (
              <div style={{ marginBottom: 6 }}>
                {d.required_stds.map((s) => (
                  <span className="badge neutral" key={s} style={{ marginRight: 6 }}>{s}</span>
                ))}
              </div>
            )}
            {(d.generated_text || d.justification) && (
              <div className="spr-text">{d.generated_text || d.justification}</div>
            )}
          </div>
        );
      })}
    </>
  );
}

// Mutlak tarih+saat (ui-admin §4: "3 gün önce" tek başına yetmez).
function fmtDT(s?: string): string {
  if (!s) return "—";
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return "—";
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

function fmtMoney(n: number, cur: "USD" | "TL"): string {
  return `${n.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${cur === "USD" ? "$" : "₺"}`;
}

// GSPR oturum profilinden ürün adını çıkar — profil şeması esnek olduğu için
// birkaç olası anahtarı sırayla dener, bulamazsa "—".
function productOf(p?: Record<string, unknown>): string {
  if (!p) return "—";
  for (const k of ["product_name", "productName", "device_name", "name"]) {
    const v = p[k];
    if (typeof v === "string" && v.trim()) return v;
  }
  return "—";
}

export default function CustomerDetail() {
  const { id } = useParams();
  const customerID = Number(id);

  const [tab, setTab] = useState<Tab>("ozet");

  const [cust, setCust] = useState<Customer | null>(null);
  const [staff, setStaff] = useState<StaffRow[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [billing, setBilling] = useState<BillingAddress[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  // --- Geçmiş sekmeleri: TEMBEL yüklenir (sekmeye ilk girişte) ---
  // Hepsini açılışta çekmek 8 istek demek; süperadmin çoğu zaman tek sekmeye
  // bakmaya geliyor (ui-admin: "bir şeye bakmaya veya düzeltmeye gelir").
  const [ov, setOv] = useState<CustomerOverview | null>(null);
  const [docs, setDocs] = useState<GeneratedDoc[] | null>(null);
  const [sessions, setSessions] = useState<GSPRSession[] | null>(null);
  const [pay, setPay] = useState<PaymentsResult | null>(null);
  const [ledger, setLedger] = useState<CreditEntry[] | null>(null);
  const [analyses, setAnalyses] = useState<AnalysisEntry[] | null>(null);
  const [chats, setChats] = useState<ChatSessionRow[] | null>(null);
  const [tabBusy, setTabBusy] = useState(false);

  // Seçili kayıt detayları (satıra tıkla → altta aç)
  const [selDoc, setSelDoc] = useState<GeneratedDoc | null>(null);
  const [selAnalysis, setSelAnalysis] = useState<AnalysisEntry | null>(null);

  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [password, setPassword] = useState("");
  const [saving, setSaving] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const [c, s, p, b] = await Promise.all([
        getCustomer(customerID),
        listStaff(customerID),
        listProjects(customerID),
        listBillingAddresses(customerID),
      ]);
      setCust(c);
      setStaff(s);
      setProjects(p);
      setBilling(b);
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : "Yükleme hatası");
    } finally {
      setLoading(false);
    }
  }, [customerID]);

  useEffect(() => {
    if (!Number.isNaN(customerID)) void reload();
  }, [customerID, reload]);

  // Sekmeye ilk girişte o sekmenin verisini çek. Her yükleyici bağımsız —
  // biri hata verse diğer sekmeler çalışır.
  useEffect(() => {
    if (Number.isNaN(customerID)) return;
    let alive = true;
    const run = async (fn: () => Promise<void>) => {
      setTabBusy(true);
      try {
        await fn();
      } catch (e: unknown) {
        if (alive) setErr(e instanceof Error ? e.message : "Yükleme hatası");
      } finally {
        if (alive) setTabBusy(false);
      }
    };

    if (tab === "ozet" && ov === null) {
      void run(async () => {
        const d = await getCustomerOverview(customerID);
        if (alive) setOv(d);
      });
    } else if (tab === "gspr" && docs === null) {
      void run(async () => {
        const [d, s] = await Promise.all([
          listCustomerDocuments(customerID),
          listCustomerGSPRSessions(customerID),
        ]);
        if (alive) { setDocs(d); setSessions(s); }
      });
    } else if (tab === "satinalma" && pay === null) {
      void run(async () => {
        const [p, l] = await Promise.all([
          listCustomerPayments(customerID),
          listCustomerCreditLedger(customerID),
        ]);
        if (alive) { setPay(p); setLedger(l); }
      });
    } else if (tab === "analiz" && analyses === null) {
      void run(async () => {
        const [a, c] = await Promise.all([
          listCustomerAnalyses(customerID),
          listCustomerChatSessions(customerID),
        ]);
        if (alive) { setAnalyses(a); setChats(c); }
      });
    }
    return () => { alive = false; };
  }, [tab, customerID, ov, docs, pay, analyses]);

  // Doküman satırına tıkla → tam snapshot (profile + spr_map + gap analizi).
  async function openDoc(d: GeneratedDoc) {
    if (selDoc?.id === d.id) { setSelDoc(null); return; }
    try {
      setSelDoc(await getCustomerDocument(customerID, d.id));
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : "Doküman açılamadı");
    }
  }

  async function openAnalysis(a: AnalysisEntry) {
    if (selAnalysis?.id === a.id) { setSelAnalysis(null); return; }
    try {
      setSelAnalysis(await getCustomerAnalysis(customerID, a.id));
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : "Analiz açılamadı");
    }
  }

  async function patch(p: Partial<Pick<Customer, "status">>) {
    if (!cust) return;
    try {
      await updateCustomer(cust.id, p);
      await reload();
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : "Güncelleme hatası");
    }
  }

  async function addCredits() {
    if (!cust) return;
    const raw = window.prompt(`${cust.name} için eklenecek kredi adedi (çıkarmak için negatif):`, "1");
    if (raw == null) return;
    const n = parseInt(raw, 10);
    if (!n || Number.isNaN(n)) return;
    try {
      await grantCredits(cust.id, n);
      await reload();
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : "Kredi tanımlanamadı");
    }
  }

  async function onCreateStaff(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim() || !password) return;
    setSaving(true);
    setErr(null);
    try {
      await createStaff(customerID, { email, password, full_name: fullName });
      setEmail("");
      setFullName("");
      setPassword("");
      await reload();
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : "Oluşturma hatası");
    } finally {
      setSaving(false);
    }
  }

  async function toggleActive(s: StaffRow) {
    try {
      await setStaffActive(customerID, s.id, !s.is_active);
      await reload();
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : "Güncelleme hatası");
    }
  }

  return (
    <div className="page">
      <p className="crumb">
        <Link to="/customers">← Müşteriler</Link>
      </p>

      {loading ? (
        <p className="muted">Yükleniyor…</p>
      ) : !cust ? (
        <>
          <h1>Müşteri bulunamadı</h1>
          {err && <div className="error">{err}</div>}
        </>
      ) : (
        <>
          <div className="page-head">
            <div className="page-head-row">
              <h1>{cust.name}</h1>
              <span className="muted">#{cust.id}</span>
              <span className={"badge " + (cust.status === "active" ? "ok" : "off")} style={{ alignSelf: "center" }}>{cust.status}</span>
            </div>
            <p className="page-sub">{cust.type === "kurumsal" ? "Kurumsal müşteri" : "Bireysel müşteri"}{cust.email ? ` · ${cust.email}` : ""}</p>
          </div>

          {err && <div className="error">{err}</div>}

          <div className="tabs">
            {([
              { key: "ozet", label: "Özet", count: undefined },
              { key: "bilgi", label: "Bilgi", count: undefined },
              { key: "gspr", label: "GSPR & Doküman", count: docs?.length },
              { key: "satinalma", label: "Satın Alma", count: pay?.payments.length },
              { key: "analiz", label: "Analiz & Sohbet", count: analyses?.length },
              { key: "personel", label: "Personel", count: staff.length },
              { key: "proje", label: "Proje", count: projects.length },
              { key: "fatura", label: "Fatura Adresi", count: billing.length },
            ] as { key: Tab; label: string; count?: number }[]).map((t) => (
              <button
                key={t.key}
                className={"tab" + (tab === t.key ? " active" : "")}
                onClick={() => setTab(t.key)}
              >
                {t.label}
                {t.count !== undefined && <span className="tab-count">{t.count}</span>}
              </button>
            ))}
          </div>

          {tabBusy && <p className="muted">Yükleniyor…</p>}

          {/* Özet — müşteri panelindeki Dashboard'un admin karşılığı */}
          {tab === "ozet" && ov && (
            <>
              <div className="cstat-grid">
                {[
                  { v: ov.credit_balance, l: "Kredi bakiyesi" },
                  { v: ov.documents, l: "Oluşturulan doküman" },
                  { v: ov.staff, l: "Personel" },
                  { v: ov.projects, l: "Proje" },
                  { v: ov.gspr_sessions, l: "GSPR oturumu" },
                  { v: ov.analyses, l: "Mevzuat analizi" },
                  { v: ov.chat_sessions, l: "AI sohbet" },
                  { v: ov.doc_analyses, l: "Doküman analizi" },
                ].map((s) => (
                  <div className="cstat" key={s.l}>
                    <span className="cstat-val">{s.v}</span>
                    <span className="cstat-lbl">{s.l}</span>
                  </div>
                ))}
              </div>
              <div className="card hint-card">
                Sayılar müşterinin kendi panelinde gördüğü değerlerle aynı kaynaktan gelir.
                Kalem kalem geçmiş için <strong>GSPR &amp; Doküman</strong>,{" "}
                <strong>Satın Alma</strong> ve <strong>Analiz &amp; Sohbet</strong> sekmelerine bakın.
                <br />
                <span className="muted">
                  Not: “Doküman analizi” yalnızca bir sayaçtır — kalem geçmişi tutulmuyor.
                </span>
              </div>
            </>
          )}

          {/* GSPR & Doküman — arşivin asıl değerli kısmı */}
          {tab === "gspr" && (
            <>
              <h3 className="section-title">Üretilen dokümanlar</h3>
              {docs && docs.length === 0 ? (
                <div className="card">
                  <div className="empty-state">
                    <Icon name="inbox" size={32} />
                    <p>Bu müşteri henüz doküman üretmemiş.</p>
                    <span className="muted" style={{ fontSize: 12.5 }}>
                      Doküman üretimi kredi harcar; kredi bakiyesi 0 ise müşteri üretim yapamaz.
                    </span>
                  </div>
                </div>
              ) : docs ? (
                <div className="data-table-card">
                  <div className="table-wrap">
                    <table className="table">
                      <thead>
                        <tr>
                          <th className="num">#</th>
                          <th>Ürün</th>
                          <th>Sınıf</th>
                          <th>Doküman kodu</th>
                          <th>Dil</th>
                          <th>Durum</th>
                          <th>Sürüm</th>
                          <th>Üretim</th>
                        </tr>
                      </thead>
                      <tbody>
                        {docs.map((d) => (
                          <tr
                            key={d.id}
                            className={"rowlink" + (selDoc?.id === d.id ? " sel" : "")}
                            onClick={() => void openDoc(d)}
                          >
                            <td className="num">{d.id}</td>
                            <td className="clip" title={d.product_name}>{d.product_name}</td>
                            <td>{d.risk_class || "—"}</td>
                            <td className="mono">{d.document_code || "—"}</td>
                            <td>{d.source_doc_id ? `çeviri · ${d.language || "?"}` : (d.language || "en")}</td>
                            <td>
                              <span className={"badge " + (d.status === "ready" ? "ok" : "warn")}>
                                {d.status === "ready" ? "hazır" : "üretiliyor"}
                              </span>
                            </td>
                            <td className="mono">{d.version || "—"}</td>
                            <td className="mono">{fmtDT(d.created_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : null}

              {selDoc && (
                <div className="card">
                  <div className="card-head">
                    <h2>#{selDoc.id} · {selDoc.product_name}</h2>
                    <button className="btn-soft" onClick={() => setSelDoc(null)}>Kapat</button>
                  </div>
                  <div className="detail-grid" style={{ marginBottom: 14 }}>
                    <div><span className="dt">Risk sınıfı</span><span className="dd">{selDoc.risk_class || "—"}</span></div>
                    <div><span className="dt">Doküman kodu</span><span className="dd">{selDoc.document_code || "—"}</span></div>
                    <div><span className="dt">Oturum</span><span className="dd">{selDoc.session_id || "—"}</span></div>
                    <div><span className="dt">Üretim</span><span className="dd">{fmtDT(selDoc.created_at)}</span></div>
                  </div>
                  <SPRMapView sprMap={selDoc.spr_map} />
                  <h3 className="section-title">Ürün profili (12 soru)</h3>
                  <pre className="snapbox">{JSON.stringify(selDoc.profile ?? {}, null, 2)}</pre>
                  {selDoc.gap_rows != null && (
                    <>
                      <h3 className="section-title">Gap analizi (Annex I standart–GSPR)</h3>
                      <pre className="snapbox">{JSON.stringify(selDoc.gap_rows, null, 2)}</pre>
                    </>
                  )}
                </div>
              )}

              <h3 className="section-title">GSPR oturumları (sihirbaz)</h3>
              {sessions && sessions.length === 0 ? (
                <div className="card">
                  <div className="empty-state">
                    <Icon name="inbox" size={32} />
                    <p>Kayıtlı GSPR oturumu yok.</p>
                    <span className="muted" style={{ fontSize: 12.5 }}>
                      Oturum, müşteri sihirbaza başladığında oluşur ve kalıcı saklanır.
                    </span>
                  </div>
                </div>
              ) : sessions ? (
                <div className="data-table-card">
                  <div className="table-wrap">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Oturum</th>
                          <th>Ürün</th>
                          <th>Başlangıç</th>
                          <th>Son güncelleme</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sessions.map((s) => (
                          <tr key={s.id}>
                            <td className="mono">{s.id}</td>
                            <td className="clip" title={productOf(s.profile)}>{productOf(s.profile)}</td>
                            <td className="mono">{fmtDT(s.created_at)}</td>
                            <td className="mono">{fmtDT(s.updated_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : null}
            </>
          )}

          {/* Satın Alma — ödemeler + kredi hareketleri */}
          {tab === "satinalma" && (
            <>
              {pay && (
                <div className="sumbar">
                  <div><span className="dt">Ödenen toplam</span><span className="dd">{fmtMoney(pay.paid_usd, "USD")}</span></div>
                  <div><span className="dt">Ödenen (TL)</span><span className="dd">{fmtMoney(pay.paid_tl, "TL")}</span></div>
                  <div><span className="dt">Alınan kredi</span><span className="dd">{pay.paid_credits}</span></div>
                  <div><span className="dt">Güncel bakiye</span><span className="dd">{cust.credit_balance}</span></div>
                </div>
              )}

              <h3 className="section-title">Ödemeler</h3>
              {pay && pay.payments.length === 0 ? (
                <div className="card">
                  <div className="empty-state">
                    <Icon name="inbox" size={32} />
                    <p>Bu müşterinin ödeme kaydı yok.</p>
                    <span className="muted" style={{ fontSize: 12.5 }}>
                      Krediler yalnızca admin tanımıyla verilmiş olabilir — Kredi hareketlerine bakın.
                    </span>
                  </div>
                </div>
              ) : pay ? (
                <div className="data-table-card">
                  <div className="table-wrap">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Referans</th>
                          <th className="num">Kredi</th>
                          <th className="num">USD</th>
                          <th className="num">TL</th>
                          <th className="num">Kur</th>
                          <th>Durum</th>
                          <th>Oluşturma</th>
                          <th>Ödeme</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pay.payments.map((p) => (
                          <tr key={p.id}>
                            <td className="mono">{p.reference}</td>
                            <td className="num">{p.credits}</td>
                            <td className="num">{p.amount_usd.toFixed(2)}</td>
                            <td className="num">{p.amount_tl.toFixed(2)}</td>
                            <td className="num">{p.usd_try_rate.toFixed(4)}</td>
                            <td>
                              <span className={"badge " + PAY_STATUS[p.status].cls}>{PAY_STATUS[p.status].label}</span>
                              {p.message && p.status === "failed" && (
                                // Kesme İŞARETLİ olmalı — düz slice kelime ortasında bitince
                                // "bozuk metin" gibi okunuyor. Tam metin title'da.
                                <span className="muted" style={{ fontSize: 12, marginLeft: 6 }} title={p.message}>
                                  {p.message.length > 30 ? p.message.slice(0, 30).trimEnd() + "…" : p.message}
                                </span>
                              )}
                            </td>
                            <td className="mono">{fmtDT(p.created_at)}</td>
                            <td className="mono">{fmtDT(p.paid_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : null}

              <h3 className="section-title">Kredi hareketleri</h3>
              {ledger && ledger.length === 0 ? (
                <div className="card">
                  <div className="empty-state">
                    <Icon name="inbox" size={32} />
                    <p>Kredi hareketi yok.</p>
                    <span className="muted" style={{ fontSize: 12.5 }}>
                      İlk hareket satın alma veya “Kredi ekle / çıkar” ile oluşur.
                    </span>
                  </div>
                </div>
              ) : ledger ? (
                <div className="data-table-card">
                  <div className="table-wrap">
                    <table className="table">
                      <thead>
                        <tr>
                          <th className="num">#</th>
                          <th className="num">Değişim</th>
                          <th>Sebep</th>
                          <th className="num">Sonraki bakiye</th>
                          <th>Referans</th>
                          <th>Tarih</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ledger.map((e) => (
                          <tr key={e.id}>
                            <td className="num">{e.id}</td>
                            <td className={"num " + (e.delta >= 0 ? "delta-pos" : "delta-neg")}>
                              {e.delta > 0 ? `+${e.delta}` : e.delta}
                            </td>
                            <td>{LEDGER_REASON[e.reason] ?? e.reason}</td>
                            <td className="num">{e.balance_after}</td>
                            <td className="mono">
                              {e.ref_payment_id ? `ödeme #${e.ref_payment_id}` : e.ref_document_id ? `doküman #${e.ref_document_id}` : "—"}
                            </td>
                            <td className="mono">{fmtDT(e.created_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : null}
            </>
          )}

          {/* Analiz & Sohbet */}
          {tab === "analiz" && (
            <>
              <h3 className="section-title">Mevzuat analizleri</h3>
              {analyses && analyses.length === 0 ? (
                <div className="card">
                  <div className="empty-state">
                    <Icon name="inbox" size={32} />
                    <p>Mevzuat analizi kaydı yok.</p>
                    <span className="muted" style={{ fontSize: 12.5 }}>
                      Müşteri panelindeki “Mevzuat Analizi” ekranı kullanıldığında burada görünür.
                    </span>
                  </div>
                </div>
              ) : analyses ? (
                <div className="data-table-card">
                  <div className="table-wrap">
                    <table className="table">
                      <thead>
                        <tr>
                          <th className="num">#</th>
                          <th>Soru</th>
                          <th>Kullanıcı</th>
                          <th>Model</th>
                          <th className="num">Kaynak</th>
                          <th className="num">Süre</th>
                          <th>Tarih</th>
                        </tr>
                      </thead>
                      <tbody>
                        {analyses.map((a) => (
                          <tr
                            key={a.id}
                            className={"rowlink" + (selAnalysis?.id === a.id ? " sel" : "")}
                            onClick={() => void openAnalysis(a)}
                          >
                            <td className="num">{a.id}</td>
                            <td className="clip" title={a.question}>{a.question}</td>
                            <td className="clip" title={a.user_email}>{a.user_name || a.user_email || `#${a.user_id}`}</td>
                            <td className="mono">{a.model || "—"}</td>
                            <td className="num">{a.chunks_used ?? "—"}</td>
                            <td className="num">{a.usage_ms ? `${a.usage_ms} ms` : "—"}</td>
                            <td className="mono">{fmtDT(a.created_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : null}

              {selAnalysis && (
                <div className="card">
                  <div className="card-head">
                    <h2>Analiz #{selAnalysis.id}</h2>
                    <button className="btn-soft" onClick={() => setSelAnalysis(null)}>Kapat</button>
                  </div>
                  <h3 className="section-title">Soru</h3>
                  <div className="answer-box">{selAnalysis.question}</div>
                  <h3 className="section-title">Cevap</h3>
                  <div className="answer-box">{selAnalysis.answer || "—"}</div>
                  {selAnalysis.citations != null && (
                    <>
                      <h3 className="section-title">Kaynak atıfları</h3>
                      <pre className="snapbox">{JSON.stringify(selAnalysis.citations, null, 2)}</pre>
                    </>
                  )}
                </div>
              )}

              <h3 className="section-title">AI sohbet oturumları</h3>
              {chats && chats.length === 0 ? (
                <div className="card">
                  <div className="empty-state">
                    <Icon name="inbox" size={32} />
                    <p>Sohbet oturumu yok.</p>
                    <span className="muted" style={{ fontSize: 12.5 }}>
                      Sohbet içerikleri admin panelinde gösterilmez; yalnız oturum başlıkları listelenir.
                    </span>
                  </div>
                </div>
              ) : chats ? (
                <div className="data-table-card">
                  <div className="table-wrap">
                    <table className="table">
                      <thead>
                        <tr><th>Oturum</th><th>Başlık</th><th>Başlangıç</th><th>Son mesaj</th></tr>
                      </thead>
                      <tbody>
                        {chats.map((c) => (
                          <tr key={c.id}>
                            <td className="mono">{c.id}</td>
                            <td className="clip" title={c.title}>{c.title || "—"}</td>
                            <td className="mono">{fmtDT(c.created_at)}</td>
                            <td className="mono">{fmtDT(c.updated_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : null}
            </>
          )}

          {/* Bilgi */}
          {tab === "bilgi" && (
            <>
              <div className="card">
                <div className="card-head"><h2>Genel Bilgiler</h2></div>
                <div className="detail-grid">
                  <div><span className="dt">Tip</span><span className="dd">{cust.type === "kurumsal" ? "Kurumsal" : "Bireysel"}</span></div>
                  <div><span className="dt">E-posta</span><span className="dd">{cust.email || "—"}</span></div>
                  <div>
                    <span className="dt">Durum</span>
                    <span className="dd"><span className={"badge " + (cust.status === "active" ? "ok" : "off")}>{cust.status === "active" ? "aktif" : "pasif"}</span></span>
                  </div>
                  <div>
                    <span className="dt">Kredi</span>
                    <span className="dd"><span className={"badge " + (cust.credit_balance > 0 ? "ok" : "off")}>{cust.credit_balance} kredi</span></span>
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="card-head"><h2>İşlemler</h2></div>
                <div className="detail-actions" style={{ marginTop: 0 }}>
                  <button className="btn-soft" onClick={() => patch({ status: cust.status === "active" ? "passive" : "active" })}>
                    {cust.status === "active" ? "Pasifleştir" : "Aktifleştir"}
                  </button>
                  <button className="btn-soft" onClick={addCredits}>
                    Kredi ekle / çıkar
                  </button>
                </div>
              </div>
            </>
          )}

          {/* Personel */}
          {tab === "personel" && (
            <>
              <form className="card form-inline" onSubmit={onCreateStaff}>
                <input placeholder="E-posta" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="off" required />
                <input placeholder="Ad Soyad" value={fullName} onChange={(e) => setFullName(e.target.value)} />
                <input placeholder="Parola" type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" required />
                <button type="submit" disabled={saving || !email.trim() || !password}>
                  {saving ? "Ekleniyor…" : "Personel Ekle"}
                </button>
              </form>

              <div className="data-table-card">
                <div className="data-table-toolbar">
                  <h2 className="dt-toolbar-title">Personel</h2>
                  <span className="data-count">{staff.length} kayıt</span>
                </div>
                {staff.length === 0 ? (
                  <div className="empty-state"><Icon name="inbox" size={32} /><p>Bu müşteride personel yok.</p></div>
                ) : (
                  <table className="table">
                    <thead>
                      <tr>
                        <th>#</th><th>E-posta</th><th>Ad Soyad</th><th className="nowrap">Kayıt IP'si</th><th>Kayıt cihazı</th><th>Durum</th><th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {staff.map((s) => (
                        <tr key={s.id}>
                          <td>{s.id}</td>
                          <td>{s.email}</td>
                          <td>{s.full_name || "—"}</td>
                          {/* Boş = bilinmiyor (2026-08-17 öncesi hesap ya da
                              admin'in oluşturduğu kullanıcı). Tire koyuyoruz:
                              boş hücre "veri kayıp" gibi okunur. */}
                          <td
                            className="nowrap mono"
                            title={s.register_ip ? "Self-servis kaydın yapıldığı IP" : "Kayıt IP'si bilinmiyor — bu hesap ölçüm eklenmeden önce açılmış ya da admin tarafından oluşturulmuş"}
                          >
                            {s.register_ip || "—"}
                          </td>
                          {/* IP ile birlikte anlamlı: ortak çıkış (NAT)
                              arkasındaki iki kaydı tek başına IP ayırt edemez.
                              Ham başlık title'da — özet çıkarıcı yeni bir
                              tarayıcıyı tanımazsa bakılacak yer orası. */}
                          <td
                            className="muted"
                            style={{ fontSize: 12 }}
                            title={s.register_user_agent
                              ? `${ortamCoz(s.register_user_agent).detay}\n\n${s.register_user_agent}`
                              : "Kayıt cihazı bilinmiyor — ölçüm eklenmeden önce açılmış hesap, admin'in oluşturduğu kullanıcı ya da User-Agent göndermeyen istemci"}
                          >
                            {s.register_user_agent ? ortamCoz(s.register_user_agent).ozet : "—"}
                          </td>
                          <td><span className={"badge " + (s.is_active ? "ok" : "off")}>{s.is_active ? "aktif" : "pasif"}</span></td>
                          <td className="row-actions">
                            <button className="link" onClick={() => toggleActive(s)}>
                              {s.is_active ? "Pasifleştir" : "Aktifleştir"}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          )}

          {/* Proje */}
          {tab === "proje" && (
            <div className="data-table-card">
              <div className="data-table-toolbar">
                <h2 className="dt-toolbar-title">Projeler</h2>
                <span className="data-count">{projects.length} kayıt</span>
              </div>
              {projects.length === 0 ? (
                <div className="empty-state">
                  <Icon name="inbox" size={32} />
                  <p>Bu müşteride henüz proje yok.</p>
                  <span className="muted" style={{ fontSize: 12.5 }}>Müşteri panelinden oluşturulacak.</span>
                </div>
              ) : (
                <table className="table">
                  <thead>
                    <tr>
                      <th>Proje Adı</th><th>Açıklama</th><th>Fatura Adresi</th><th>Durum</th><th>Oluşturulma</th>
                    </tr>
                  </thead>
                  <tbody>
                    {projects.map((p) => (
                      <tr key={p.id}>
                        <td>{p.name}</td>
                        <td>{p.description || "—"}</td>
                        <td>{p.billing_title || "—"}</td>
                        <td><span className={"badge " + (PROJECT_STATUS[p.status]?.cls ?? "neutral")}>{PROJECT_STATUS[p.status]?.label ?? p.status}</span></td>
                        <td>{fmtDate(p.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* Fatura Adresi */}
          {tab === "fatura" && (
            <>
              {billing.length === 0 ? (
                <div className="card">
                  <div className="empty-state">
                    <Icon name="inbox" size={32} />
                    <p>Bu müşteride henüz fatura adresi yok.</p>
                    <span className="muted" style={{ fontSize: 12.5 }}>Müşteri panelinden oluşturulacak.</span>
                  </div>
                </div>
              ) : (
                /* Akordiyon: müşteride birden çok fatura adresi olabiliyor ve
                   hepsi açık gelince sekme çok uzuyordu. Başlangıçta HEPSİ
                   KAPALI; yalnız başlık ve tip rozeti görünür, istenen açılır.
                   Native <details> kullanıldı — JS durumu, klavye desteği ve
                   erişilebilirlik ücretsiz gelir. */
                billing.map((b) => (
                  <details className="card acc-card" key={b.id}>
                    <summary className="acc-summary">
                      <span className="acc-chev" aria-hidden="true">
                        <Icon name="chevronRight" size={16} />
                      </span>
                      <span className="acc-title">{b.title}</span>
                      <span className="badge neutral">{b.address_type === "bireysel" ? "Bireysel" : "Kurumsal"}</span>
                    </summary>
                    <div className="detail-grid">
                      {b.address_type === "bireysel" ? (
                        <>
                          <div><span className="dt">Ad Soyad</span><span className="dd">{[b.first_name, b.last_name].filter(Boolean).join(" ") || "—"}</span></div>
                          <div><span className="dt">TC Kimlik No</span><span className="dd">{b.tc_kimlik_no || "—"}</span></div>
                        </>
                      ) : (
                        <>
                          <div><span className="dt">Firma Ünvanı</span><span className="dd">{b.company_name || "—"}</span></div>
                          <div><span className="dt">Vergi Dairesi</span><span className="dd">{b.tax_office || "—"}</span></div>
                          <div><span className="dt">Vergi No</span><span className="dd">{b.tax_no || "—"}</span></div>
                        </>
                      )}
                      <div><span className="dt">Ülke</span><span className="dd">{b.country || "—"}</span></div>
                      <div><span className="dt">Eyalet</span><span className="dd">{b.state || "—"}</span></div>
                      <div><span className="dt">İl</span><span className="dd">{b.city || "—"}</span></div>
                      <div><span className="dt">Posta Kodu</span><span className="dd">{b.postal_code || "—"}</span></div>
                      <div><span className="dt">E-posta</span><span className="dd">{b.email || "—"}</span></div>
                      <div><span className="dt">Telefon</span><span className="dd">{b.phone || "—"}</span></div>
                      <div><span className="dt">Açık Adres</span><span className="dd">{b.full_address || "—"}</span></div>
                    </div>
                  </details>
                ))
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
